import { useState } from 'react';
import type { CaptureContractRate, CaptureContractRates } from '@aithora/shared';
import { sx } from '../../ui/designStyles';
import { AboutThis } from '../../ui/AboutThis';
import { CLOSE_BTN, KICKER, PRI, SEC, chip, dayYear, monthOf, pre, railStyle, usd2, type Honesty } from './lib';
import type { Tag } from './GenTab';

/**
 * Contracts › This building and Portfolio (design pvCt). The rate grid is payer × code from
 * GET /capture/contract-rates: the Medicare rate for the building's locality (PUBLISHED),
 * Medicare Advantage as "No published rate" with the Medicare reference, and the published
 * commercial range from the price-transparency files. Your own rate per payer, percentiles,
 * renewals and the negotiation targets need an uploaded contract or remittance, which the
 * back end does not take yet: those parts show the design's own empty wording, never $0.
 */
type Kind = 'p' | 'm' | 'c';
interface Cell {
  v: string;
  s: string;
  np: boolean;
  bg: string;
  fg: string;
}
interface Row {
  id: string;
  kind: Kind;
  name: string;
  sub: string;
  gap: string;
  cells: Cell[];
}

const EMPTY_CELL = (s: string): Cell => ({ v: 'No published rate', s, np: true, bg: 'transparent', fg: '#54606C' });

function grid(r: CaptureContractRates, pos: 'nf' | 'f'): { codes: CaptureContractRate[]; rows: Row[] } {
  const codes = r.codes;
  const med = (c: CaptureContractRate) => (pos === 'f' ? c.medicare.facility : c.medicare.nonFacility);
  const year = r.schedule.year;
  const rows: Row[] = [
    {
      id: 'mcr', kind: 'p', name: 'Medicare FFS', sub: 'PFS ' + year + (r.locality ? ' · ' + placeName(r.locality.name) : ' · national'), gap: 'fixed',
      cells: codes.map((c) => { const v = med(c).value; return v == null ? EMPTY_CELL(pos === 'f' ? 'facility setting' : 'not on the schedule') : { v: usd2(v), s: 'published', np: false, bg: 'rgba(34,39,46,.04)', fg: '#3C454F' }; }),
    },
    {
      id: 'ma', kind: 'm', name: 'Medicare Advantage', sub: 'not in public files', gap: 'unknown',
      cells: codes.map((c) => { const v = med(c).value; return EMPTY_CELL(v == null ? 'no reference' : 'ref $' + Math.round(v / 100)); }),
    },
    {
      id: 'com', kind: 'c', name: 'Commercial, published range', sub: (() => { const p = new Set(codes.flatMap((c) => c.market.professional?.payers ?? [])); return p.size ? p.size + ' payers · rate files' : 'price-transparency files'; })(), gap: '–',
      cells: codes.map((c) => {
        const m = c.market.professional ?? c.market.institutional;
        if (c.market.status === 'published' && m?.medianCents != null) return { v: usd2(m.medianCents), s: 'median of ' + m.count, np: false, bg: 'rgba(34,39,46,.05)', fg: '#22272E' };
        return { ...EMPTY_CELL(c.market.status === 'not_checked' ? '' : 'none in the files'), v: c.market.status === 'not_checked' ? 'Not yet checked' : 'No published rate' };
      }),
    },
  ];
  return { codes, rows };
}

export function Contracts({ rates, error, scope, tag, narrow, railOpen, setRailOpen, now, access }: {
  rates: CaptureContractRates | null;
  error: string | null;
  scope: 'bld' | 'port';
  tag: Tag;
  narrow: boolean;
  railOpen: boolean;
  setRailOpen: (o: boolean) => void;
  now: number;
  /** The Facility Admin's code-level access request, when codes are hidden (null: no request to offer). */
  access?: { pending: boolean; off: string | null; go: () => void } | null;
}) {
  const [pos, setPos] = useState<'nf' | 'f'>('nf');
  const [show, setShow] = useState<'Your rate' | 'Percentile' | 'Gap'>('Your rate');
  const [sel, setSel] = useState<[string, number]>(['mcr', 0]);
  const g = rates ? grid(rates, pos) : { codes: [], rows: [] };
  const region = rates?.locality ? placeName(rates.locality.name) : 'your region';
  // Codes hidden and no service name either: seven identical "Code hidden" columns would say
  // nothing, so the grid gives way to one plain state.
  const hidden = !!rates && rates.codes.length > 0 && rates.codes.every((c) => !c.code && !c.description);
  const n = hidden ? 0 : g.codes.length;
  // Sized to fit the table at 1280 and 1568 wide; narrower, it scrolls with the payer and the
  // money-left columns pinned, so neither is ever cut. With no codes yet (no building picked),
  // the money-left heading sits at the right edge, not in an empty code column.
  const cols = n ? 'minmax(116px,1.4fr) repeat(' + n + ',minmax(58px,1fr)) minmax(84px,.8fr)' : 'minmax(116px,1fr) max-content';
  const minW = 116 + n * 58 + 84 + (n + 1) * 4 + 20;
  const end = new Date(now + 180 * 86_400_000);
  const endLbl = end.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  const selRow = g.rows.find((r) => r.id === sel[0]) ?? g.rows[0] ?? null;
  const selCode = g.codes[sel[1]] ?? g.codes[0] ?? null;
  const pub: Honesty | null = rates ? (rates.real ? 'PUBLISHED' : 'SAMPLE') : null;
  const fileDates = [...new Set(g.codes.flatMap((c) => c.market.professional?.fileDates ?? []))].sort();
  const srcDate = fileDates.length ? monthOf(fileDates[fileDates.length - 1]) : null;

  let brief: { title: string; sub: string; why: string; whyBg: string; levT: string; lev: { a: string; b: string; c: string }[]; srcYou: string; srcRange: string; acts: { t: string; primary: boolean; off: string }[]; dist: CaptureContractRate | null } | null = null;
  if (selRow && selCode && !hidden) {
    const ref = pos === 'f' ? selCode.medicare.facility.value : selCode.medicare.nonFacility.value;
    const code = selCode.code ?? 'Code hidden';
    const sched = rates ? 'Physician Fee Schedule ' + rates.schedule.year + ', ' + rates.schedule.release + ', released ' + dayYear(rates.schedule.releasedOn) : '';
    const UPLOAD_OFF = 'Contract and remittance upload is not built yet.';
    if (selRow.kind === 'p')
      brief = { title: 'Medicare FFS · ' + code, sub: 'Fixed published rate', why: 'CMS sets this rate. Nothing to negotiate. It is the reference for every other payer.', whyBg: 'rgba(34,39,46,.05)', levT: 'Rate', lev: [{ a: code + (selCode.description ? ' ' + selCode.description : ''), b: ref == null ? 'No published rate' : usd2(ref), c: '#22272E' }], srcYou: sched, srcRange: 'n/a', acts: [], dist: null };
    else if (selRow.kind === 'm')
      brief = { title: 'Medicare Advantage · ' + code, sub: 'renewal date unknown', why: 'Medicare Advantage rates are not in the public files. Upload a remittance or your contract and we compare it with the Medicare rate' + (ref == null ? '.' : ' of ' + usd2(ref) + '.'), whyBg: 'rgba(34,39,46,.05)', levT: 'What we can already see', lev: [{ a: 'Medicare reference', b: ref == null ? 'No published rate' : usd2(ref), c: '#22272E' }], srcYou: 'No published rate', srcRange: 'Not published for Medicare Advantage', acts: [{ t: 'Upload remittance', primary: true, off: UPLOAD_OFF }, { t: 'Upload contract', primary: false, off: UPLOAD_OFF }], dist: null };
    else {
      const m = selCode.market.professional ?? selCode.market.institutional;
      const has = selCode.market.status === 'published' && m?.medianCents != null;
      brief = {
        title: 'Commercial · ' + code, sub: has ? m!.count + ' published rates · ' + (m!.payers.length || 0) + ' payers' : 'No published rate',
        why: has ? 'What insurers publish for ' + code + ' in ' + region + '. Upload your contract or a remittance to see where your own rate sits and what to ask for at renewal.' : selCode.market.text ?? 'No published commercial rate for this code in your region.',
        whyBg: 'rgba(34,39,46,.05)', levT: 'What we can already see',
        lev: [{ a: 'Medicare, same code', b: ref == null ? 'No published rate' : usd2(ref), c: '#22272E' }, ...(has ? [{ a: 'Published median', b: usd2(m!.medianCents!), c: '#22272E' }] : [])],
        srcYou: 'No contract or remittance on file', srcRange: has ? (m!.payers.join(', ') || 'Insurer') + ' price-transparency rate files' + (m!.fileDates.length ? ', ' + monthOf(m!.fileDates[m!.fileDates.length - 1]) : '') : 'No published rate',
        acts: [{ t: 'Upload contract', primary: true, off: UPLOAD_OFF }], dist: has ? selCode : null,
      };
    }
  }
  const pct = (cents: number, lo: number, hi: number) => Math.max(2, Math.min(98, ((cents - lo) / (hi - lo || 1)) * 100)) + '%';

  return (
    <div data-screen-label="Capture Contracts" style={sx('flex:1 1 auto;min-height:0;display:flex;flex-direction:column;gap:10px;')}>
      <div style={sx('flex:0 0 auto;display:flex;align-items:center;flex-wrap:nowrap;gap:8px;')}>
        <span
          style={sx('flex:1 1 auto;min-width:0;font-size:16px;color:#54606C;')}
          title={'Your rate for each code and payer against what that payer publishes in ' + region + '. Public files and your own contracts only.'}
        >
          {'Your rates vs. what each payer publishes in ' + region + ' · public files and your contracts only'}
        </span>
        <span title="Contract and remittance upload is not built yet." style={sx('flex:0 0 auto;height:30px;padding:0 11px;display:inline-flex;align-items:center;border:1px solid rgba(34,39,46,.2);border-radius:7px;font-size:16px;font-weight:700;white-space:nowrap;')}>
          Upload contract or remittance
        </span>
      </div>
      <div style={sx('flex:0 0 auto;display:flex;align-items:stretch;gap:6px;')}>
        <span style={sx('font-size:14px;font-weight:700;color:#3C454F;align-self:center;white-space:nowrap;margin-right:4px;')}>Renewals, next 180 days</span>
        <div style={sx('flex:1 1 auto;position:relative;min-height:40px;box-sizing:border-box;padding:4px 8px 16px;border-radius:7px;background:rgba(34,39,46,.04);border:1px solid rgba(34,39,46,.1);')}>
          <span style={sx('position:absolute;left:0;top:0;bottom:0;width:50%;background:rgba(179,38,30,.06);border-right:1px dashed rgba(179,38,30,.4);')} />
          <span style={sx('position:absolute;left:4px;bottom:2px;font-size:14px;color:#54606C;')}>today</span>
          <span style={sx('position:absolute;left:calc(50% + 4px);bottom:2px;font-size:14px;color:#B3261E;')}>90-day alert line</span>
          <span style={sx('position:absolute;right:4px;bottom:2px;font-size:14px;color:#54606C;')}>{endLbl}</span>
          {/* In the flow above the axis words, so a longer line (Spanish) wraps and the strip grows instead of running into them. */}
          <span style={sx('position:relative;display:block;text-align:center;font-size:14px;font-weight:700;line-height:1.25;color:#54606C;')}>
            {scope === 'port' ? 'No contracts on file for these buildings. Renewal dates come from uploaded contracts.' : 'No contracts on file. Renewal dates come from uploaded contracts.'}
          </span>
        </div>
      </div>
      <div style={sx('flex:0 0 auto;display:flex;align-items:center;flex-wrap:wrap;gap:6px;')}>
        <span style={sx('font-size:14px;color:#54606C;')}>Setting</span>
        {([['nf', 'Assisted living · non-facility rate'], ['f', 'Nursing facility · facility rate']] as const).map(([k, l]) => (
          <a key={k} href="#" onClick={(e) => { pre(e); setPos(k); }} style={sx(chip(pos === k))} aria-pressed={pos === k}>{l}</a>
        ))}
        <span style={sx('width:1px;height:20px;background:rgba(34,39,46,.15);margin:0 4px;')} />
        <span style={sx('font-size:14px;color:#54606C;')}>Show</span>
        {(['Your rate', 'Percentile', 'Gap'] as const).map((k) => (
          <a key={k} href="#" onClick={(e) => { pre(e); setShow(k); }} style={sx(chip(show === k))} aria-pressed={show === k}>{k === 'Gap' ? 'Gap to median' : k}</a>
        ))}
        <span style={sx('width:1px;height:20px;background:rgba(34,39,46,.15);margin:0 4px;')} />
        <span style={sx('display:flex;gap:10px;align-items:center;font-size:14px;color:#3C454F;')}>
          <span style={sx(LG)}><span style={sx(SQ + 'background:rgba(179,38,30,.16);border:1px solid #B3261E;')} />below 25th pct</span>
          <span style={sx(LG)}><span style={sx(SQ + 'background:rgba(107,74,18,.14);border:1px solid #8A5A00;')} />25th–50th</span>
          <span style={sx(LG)}><span style={sx(SQ + 'background:rgba(34,39,46,.06);border:1px solid #54606C;')} />50th–75th</span>
          <span style={sx(LG)}><span style={sx(SQ + 'background:rgba(31,107,82,.15);border:1px solid #1F6B52;')} />above 75th</span>
          <span style={sx(LG)}><span style={sx(SQ + 'border:1px dashed rgba(34,39,46,.35);')} />no published rate</span>
        </span>
        <span style={sx('flex:1 1 10px;')} />
        <span style={sx('font-size:14px;color:#54606C;')}>Rows sorted by money left on the table</span>
      </div>
      <div style={sx('flex:1 1 auto;min-height:360px;display:grid;grid-template-columns:' + (narrow ? 'minmax(0,1fr)' : 'minmax(0,1fr) minmax(250px,30%)') + ';gap:12px;position:relative;')}>
        <div
          onClickCapture={(e) => { if (narrow && !railOpen && (e.target as HTMLElement).closest?.('a[data-cell]')) setRailOpen(true); }}
          style={sx('min-width:0;min-height:0;display:flex;flex-direction:column;border:1px solid rgba(34,39,46,.12);border-radius:8px;background:#FCFAF4;overflow:auto;')}
        >
          {!hidden && <div style={sx('flex:0 0 auto;position:sticky;top:0;z-index:2;min-width:' + minW + 'px;display:grid;grid-template-columns:' + cols + ';gap:0 4px;padding:0 10px;background:#F6F3EC;border-bottom:1px solid rgba(34,39,46,.14);')}>
            <span style={sx('padding:8px 0;font-size:14px;font-weight:700;color:#54606C;position:sticky;left:0;z-index:1;background:#F6F3EC;')}>Payer · members</span>
            {!hidden && g.codes.map((c) => (
              <span key={c.codeKey} style={sx('padding:5px 0;display:flex;flex-direction:column;align-items:center;min-width:0;')}>
                <span style={sx('font-size:14px;font-weight:800;color:#22272E;text-align:center;')} title={c.code ?? c.description ?? undefined}>{c.code ?? (short(c.description) || 'Code hidden')}</span>
                <span title={c.description ?? undefined} style={sx('font-size:14px;color:#54606C;text-align:center;')}>{c.code ? short(c.description) : ''}</span>
              </span>
            ))}
            <span style={sx('padding:8px 0;font-size:14px;font-weight:700;color:#54606C;text-align:right;position:sticky;right:0;z-index:1;box-shadow:-6px 0 6px -6px rgba(34,39,46,.25);background:#F6F3EC;')}>Below median / yr</span>
          </div>}
          <div style={sx('flex:1 0 auto;')}>
            {hidden && (
              <div data-testid="capture-codes-hidden" style={sx('margin:16px;padding:14px 16px;display:flex;align-items:center;flex-wrap:wrap;gap:10px 14px;border-radius:8px;border:1px solid rgba(47,92,151,.3);background:rgba(47,92,151,.06);')}>
                <span style={sx('flex:1 1 260px;min-width:0;display:flex;flex-direction:column;gap:2px;line-height:1.4;')}>
                  <span style={sx('font-size:16px;font-weight:800;color:#22272E;')}>Codes are hidden for your role</span>
                  <span style={sx('font-size:16px;color:#54606C;')}>Rates by payer and code show here once code-level access is on.</span>
                </span>
                {access &&
                  (access.pending ? (
                    <span style={sx('font-size:14px;font-weight:700;color:#1F6B52;')}>Requested · Aithora confirms within one business day</span>
                  ) : (
                    <a
                      href="#"
                      aria-disabled={access.off ? 'true' : undefined}
                      title={access.off ?? undefined}
                      onClick={(e) => { pre(e); if (!access.off) access.go(); }}
                      style={sx(PRI + (access.off ? 'opacity:.55;cursor:not-allowed;' : ''))}
                    >
                      Request code-level access
                    </a>
                  ))}
              </div>
            )}
            {!hidden && g.rows.map((r) => (
              <div key={r.id} style={sx('min-width:' + minW + 'px;display:grid;grid-template-columns:' + cols + ';gap:4px;align-items:center;padding:4px 10px;border-bottom:1px solid rgba(34,39,46,.07);')}>
                <span style={sx('min-width:0;display:flex;flex-direction:column;align-self:stretch;justify-content:center;position:sticky;left:0;z-index:1;background:#FCFAF4;')}>
                  <span style={sx('font-size:16px;font-weight:700;line-height:1.25;overflow-wrap:anywhere;')}>{r.name}</span>
                  <span style={sx('font-size:14px;color:#54606C;')} title={r.sub}>{r.sub}</span>
                </span>
                {r.cells.map((c, ci) => {
                  const on = sel[0] === r.id && sel[1] === ci;
                  return (
                    <a
                      key={ci}
                      href="#"
                      data-cell="1"
                      onClick={(e) => { pre(e); setSel([r.id, ci]); }}
                      aria-current={on ? 'true' : undefined}
                      style={sx('min-width:0;min-height:40px;display:flex;flex-direction:column;align-items:center;justify-content:center;border-radius:6px;text-decoration:none;overflow:hidden;text-align:center;line-height:1.15;padding:0 3px;' + (c.np ? 'border:1px dashed rgba(34,39,46,.3);' : '') + 'background:' + c.bg + ';color:' + c.fg + ';' + (on ? 'box-shadow:inset 0 0 0 2px #22272E;' : ''))}
                    >
                      <span style={sx('font-size:' + (c.np ? '14px' : '16px') + ';font-weight:700;font-variant-numeric:tabular-nums;')}>{c.v}</span>
                      <span style={sx('font-size:14px;line-height:1.15;opacity:.85;')}>{c.s}</span>
                    </a>
                  );
                })}
                <span style={sx('text-align:right;display:flex;flex-direction:column;align-self:stretch;justify-content:center;position:sticky;right:0;z-index:1;box-shadow:-6px 0 6px -6px rgba(34,39,46,.25);background:#FCFAF4;')}>
                  <span style={sx('font-size:16px;font-weight:800;font-variant-numeric:tabular-nums;color:#54606C;')}>{r.gap}</span>
                  <span style={sx('font-size:14px;color:#6B4A12;')} />
                </span>
              </div>
            ))}
            {!rates && <div style={sx('padding:24px;font-size:16px;color:#54606C;')}>{error ?? ''}</div>}
          </div>
          {/* V9: the source note opens on the i button (design k11444). */}
          <AboutThis inline testId="contracts-source" textStyle="flex:0 0 auto;padding:6px 10px;border-top:1px solid rgba(34,39,46,.12);font-size:14px;color:#54606C;">
            {'Ranges from insurer rate files published under federal price-transparency rules' + (srcDate ? ', ' + srcDate : '') + '. Medicare Advantage rates are not in those files. Every rate shows its source and date in the panel. Counsel review required before release.'}
            {pub && tag(pub) ? '' : ''}
          </AboutThis>
        </div>
        <div style={sx(railStyle(narrow, railOpen))} data-testid="capture-rail">
          {narrow && <a href="#" onClick={(e) => { pre(e); setRailOpen(false); }} style={sx(CLOSE_BTN)}>Close ✕</a>}
          {!brief && <span style={sx('font-size:16px;color:#54606C;')}>{error ?? (hidden ? 'The negotiation brief needs the codes. It opens here once code-level access is on.' : 'Pick a rate to see the brief.')}</span>}
          {brief && (
            <>
              <div style={sx('display:flex;flex-direction:column;gap:2px;')}>
                <span style={sx(KICKER)}>NEGOTIATION BRIEF</span>
                <span style={sx('font-size:17px;font-weight:800;line-height:1.2;')}>{brief.title}</span>
                <span style={sx('font-size:14px;color:#54606C;')}>{brief.sub}</span>
              </div>
              <span style={sx('font-size:16px;line-height:1.4;padding:8px 10px;border-radius:7px;background:' + brief.whyBg + ';')}>{brief.why}</span>
              {brief.dist && (() => {
                const m = brief.dist.market.professional ?? brief.dist.market.institutional!;
                const lo = Math.min(m.p25Cents ?? m.medianCents!, m.medianCents!) * 0.8;
                const hi = Math.max(m.p75Cents ?? m.medianCents!, m.medianCents!) * 1.2;
                return (
                  <div style={sx('display:flex;flex-direction:column;gap:4px;')}>
                    <span style={sx('font-size:14px;font-weight:700;color:#3C454F;')}>The published range</span>
                    <div style={sx('position:relative;height:46px;')}>
                      <span style={sx('position:absolute;left:0;right:0;top:16px;height:10px;border-radius:5px;background:rgba(34,39,46,.08);')} />
                      {m.p25Cents != null && m.p75Cents != null && (
                        <span style={sx('position:absolute;left:' + pct(m.p25Cents, lo, hi) + ';width:' + (parseFloat(pct(m.p75Cents, lo, hi)) - parseFloat(pct(m.p25Cents, lo, hi))) + '%;top:16px;height:10px;background:rgba(47,92,151,.28);')} />
                      )}
                      <span style={sx('position:absolute;left:' + pct(m.medianCents!, lo, hi) + ';top:12px;width:2px;height:18px;background:#2F5C97;')} />
                    </div>
                    <span style={sx('display:flex;justify-content:space-between;font-size:14px;color:#54606C;font-variant-numeric:tabular-nums;')}>
                      <span>{m.p25Cents != null ? '25th ' + usd2(m.p25Cents) : ''}</span>
                      <span>{'median ' + usd2(m.medianCents!)}</span>
                      <span>{m.p75Cents != null ? '75th ' + usd2(m.p75Cents) : ''}</span>
                    </span>
                  </div>
                );
              })()}
              <div style={sx('display:flex;flex-direction:column;')}>
                <span style={sx('font-size:14px;font-weight:700;color:#3C454F;padding-bottom:4px;')}>{brief.levT}</span>
                {brief.lev.map((l) => (
                  <div key={l.a} style={sx('display:grid;grid-template-columns:minmax(0,1fr) minmax(0,1fr);gap:8px;padding:5px 0;border-top:1px solid rgba(34,39,46,.08);font-size:14px;')}>
                    <span style={sx('min-width:0;')}>{l.a}</span>
                    <span style={sx('min-width:0;text-align:right;font-weight:700;font-variant-numeric:tabular-nums;color:' + l.c + ';')}>{l.b}</span>
                  </div>
                ))}
              </div>
              <div style={sx('display:flex;flex-direction:column;gap:2px;font-size:14px;color:#54606C;')}>
                <span><b style={sx('color:#3C454F;')}>Your rate:</b> {brief.srcYou}</span>
                <span><b style={sx('color:#3C454F;')}>Range:</b> {brief.srcRange}</span>
              </div>
              <div style={sx('display:flex;gap:6px;flex-wrap:wrap;margin-top:auto;')}>
                {brief.acts.map((a) => (
                  <span key={a.t} title={a.off} aria-disabled="true" style={sx((a.primary ? PRI : SEC) + 'opacity:.55;cursor:not-allowed;')}>{a.t}</span>
                ))}
              </div>
              <span style={sx('font-size:14px;color:#54606C;')}>Atlas drafts. A person reviews and sends. Nothing is sent automatically.</span>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const LG = 'display:inline-flex;gap:4px;align-items:center;';
const SQ = 'width:12px;height:12px;border-radius:3px;';
/** The code's short name under it ("CCM", "RPM mgmt"): the description's first words. */
const short = (d: string | null | undefined) => (d ? d.split(/[,(·]/)[0].trim().split(' ').slice(0, 3).join(' ') : '');
/** "Austin" for the fee schedule's "AUSTIN"; a name already in mixed case ("East Texas") stays. */
const SMALL = new Set(['of', 'the', 'and', 'in']);
export const placeName = (n: string) =>
  n !== n.toUpperCase() ? n : n.toLowerCase().replace(/[a-z]+/g, (w, i: number) => (i > 0 && SMALL.has(w) ? w : w.charAt(0).toUpperCase() + w.slice(1)));
